import { useEffect, useMemo } from 'react'
import { Configure, Hits, InstantSearch, SearchBox } from 'react-instantsearch'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import 'instantsearch.css/themes/algolia.css'
import { XIcon } from 'lucide-react'

export const HeaderSearchPopup = ({
	type,
	isOpen,
	onClose,
	locale,
}: {
	type: 'website' | 'magazine'
	isOpen: boolean
	onClose: () => void
	locale?: string
}) => {
	useEffect(() => {
		if (!isOpen) {
			return
		}

		document.body.style.overflow = 'hidden'

		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose()
			}
		}
		document.addEventListener('keyup', handleKeyUp)
		return () => {
			document.removeEventListener('keyup', handleKeyUp)
			document.body.style.overflow = ''
		}
	}, [isOpen, onClose])

	const searchClient = useMemo(() => {
		const additionalSearchParameters: Record<string, string> = {
			query_by: 'title,lead,subtitle,subtitleSecondary,navigation,content',
		}

		if (locale) {
			additionalSearchParameters.filter_by = `locale:=${locale} || site:=${locale}`
		}

		const adapter = new TypesenseInstantSearchAdapter({
			server: {
				apiKey: process.env.NEXT_PUBLIC_TS_API_KEY || '',
				nodes: [
					{
						host: process.env.NEXT_PUBLIC_TS_HOST || '',
						port: Number.parseInt(process.env.NEXT_PUBLIC_TS_PORT || '443', 10),
						protocol: process.env.NEXT_PUBLIC_TS_PROTOCOL || 'https',
					},
				],
			},
			additionalSearchParameters,
		})
		return adapter.searchClient
	}, [locale])

	if (!isOpen) {
		return null
	}

	const cleanSearchText = (text: string) => {
		if (!text) return null

		return text
			.replace(/&lt;br\s*\/?&gt;/gi, ' ')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&amp;/g, '&')
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'")
			.replace(/<br\s*\/?>/gi, ' ')
			.replace(/\s+/g, ' ')
			.trim()
	}

	// Function to parse and render highlighted text
	const renderHighlightedText = (text: string) => {
		if (!text) return null

		// Split by <mark> or <em> tags
		const cleanedText = cleanSearchText(text)
		const parts = cleanedText?.split(/<\/?(?:mark|em)>/g)

		// Every odd index part needs highlighting (1, 3, 5, etc.)
		return parts?.map((part, index) => {
			return index % 2 === 1 ? (
				<span key={index} className="bg-yellow-200 font-bold">
					{part}
				</span>
			) : (
				part || null
			)
		})
	}

	const Hit = ({
		hit,
	}: {
		hit: {
			title: string
			url: string
			lead?: string
			navigation?: string
			api: 'website' | 'magazine'
			_highlightResult?: {
				title?: { value: string }
				lead?: { value: string }
				navigation?: { value: string }
			}
		}
	}) => {
		const api =
			type === 'magazine'
				? hit.api === 'magazine'
					? hit.url
					: `${process.env.NEXT_PUBLIC_WEBSITE_URL}${hit.url}`
				: hit.api === 'website'
					? hit.url
					: `${process.env.NEXT_PUBLIC_MAGAZINE_URL}${hit.url}`

		// Determine what to display as title and subtitle
		const displayTitle = hit.navigation || hit.title
		const displaySubtitle = hit.navigation ? hit.title : hit.lead

		// Get the highlighted versions if available
		const highlightedTitle = hit.navigation
			? hit._highlightResult?.navigation?.value
			: hit._highlightResult?.title?.value
		const highlightedSubtitle = hit.navigation
			? hit._highlightResult?.title?.value
			: hit._highlightResult?.lead?.value

		return (
			<a
				href={api}
				className="block p-4 text-inherit no-underline transition-colors duration-200 border-l-4 hover:text-[#8f66ff] hover:bg-gray-100"
				onClick={onClose}
			>
				<span className="block font-semibold mb-2 text-lg">
					{highlightedTitle ? renderHighlightedText(highlightedTitle) : displayTitle}
				</span>
				{displaySubtitle ? (
					<span className="block text-sm text-gray-500">
						{highlightedSubtitle ? renderHighlightedText(highlightedSubtitle) : displaySubtitle}
					</span>
				) : null}
			</a>
		)
	}

	const handleOverlayClick = () => {
		onClose()
	}

	return (
		<div
			className="fixed inset-0 flex items-start justify-center bg-black bg-opacity-60 z-[1000] overflow-hidden pointer-events-auto"
			onClick={handleOverlayClick}
			onKeyUp={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					onClose()
				}
			}}
			role="presentation"
			tabIndex={-1}
		>
			<div
				className="max-md:mx-4 mt-[10vh] w-full max-w-[800px] max-h-[800px] flex flex-col bg-white rounded-lg py-10 px-6 shadow-lg relative z-[1001]"
				onClick={(e) => e.stopPropagation()}
				onKeyUp={(e) => {
					e.stopPropagation()
				}}
			>
				<button
					type="button"
					className="absolute top-1 right-1 bg-transparent border-0 cursor-pointer text-gray-500"
					onClick={onClose}
				>
					<XIcon />
				</button>
				<InstantSearch searchClient={searchClient} indexName="articles">
					<SearchBox autoFocus />
					<Configure hitsPerPage={8} />
					<div
						className="flex-1 overflow-y-auto mt-4 relative z-[1002]"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
						role="presentation"
					>
						<Hits
							hitComponent={Hit}
							className="[&_.ais-Hits]:mt-4 [&_.ais-Hits-list]:flex [&_.ais-Hits-list]:flex-col [&_.ais-Hits-list]:gap-3 [&_.ais-Hits-list]:m-0 [&_.ais-Hits-list]:p-0 [&_.ais-Hits-list]:list-none [&_.ais-Hits-item]:w-full [&_.ais-Hits-item]:!border-0 [&_.ais-Hits-item]:!p-0 [&_.ais-Hits-item]:m-0 [&_.ais-Hits-item]:rounded-md [&_.ais-Hits-item]:overflow-hidden [&_.ais-Hits-item]:bg-gray-50 [&_.ais-Hits-item]:shadow-none"
						/>
					</div>
				</InstantSearch>
			</div>
		</div>
	)
}
